import { useRef } from 'react';
import { useInView } from '../../hooks/useInView';

interface QAItem {
  question: string;
  answer: string;
}

interface SectionAIAnswerProps {
  heading?: string;
  items: QAItem[];
}

export default function SectionAIAnswer({ heading = 'Common Questions', items }: SectionAIAnswerProps) {
  const { ref: headRef, isVisible: headVisible } = useInView();
  const { ref: listRef, isVisible: listVisible } = useInView({ threshold: 0.05 });

  return (
    <section className="relative bg-rdr-charcoal py-24 lg:py-32 overflow-hidden">
      {/* Decorative accent */}
      <div className="absolute left-0 top-0 w-1 h-full bg-gradient-to-b from-transparent via-rdr-gold/50 to-transparent" />
      <div className="absolute right-0 top-0 w-1 h-full bg-gradient-to-b from-transparent via-rdr-gold/50 to-transparent" />

      <div className="max-w-5xl mx-auto px-6 lg:px-12">
        {/* Header */}
        <div
          ref={headRef as React.RefObject<HTMLDivElement>}
          className={`text-center mb-16 lg:mb-20 fade-up ${headVisible ? 'is-visible' : ''}`}
        >
          <div className="inline-flex items-center gap-4 mb-5">
            <span className="gold-divider" />
            <p className="text-rdr-gold text-xs tracking-[0.25em] uppercase font-sans">{heading}</p>
            <span className="gold-divider" />
          </div>
          <h2 className="font-serif text-white text-4xl lg:text-5xl xl:text-6xl leading-tight">
            Everything You Need to Know
          </h2>
        </div>

        {/* Q&A List */}
        <div
          ref={listRef as React.RefObject<HTMLDivElement>}
          className="space-y-12"
        >
          {items.map((item, i) => (
            <div
              key={i}
              className={`grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 border-b border-white/10 pb-12 fade-up ${listVisible ? 'is-visible' : ''}`}
              style={{ transitionDelay: `${i * 0.15}s` }}
            >
              <div className="lg:col-span-1 flex items-start lg:justify-end pt-1">
                <span className="font-serif text-rdr-gold/50 text-4xl leading-none">{String(i + 1).padStart(2, '0')}</span>
              </div>
              <div className="lg:col-span-11">
                <h3 className="font-serif text-white text-xl lg:text-2xl mb-4 leading-snug">
                  {item.question}
                </h3>
                <p className="text-white/55 font-sans text-base leading-relaxed">
                  {item.answer}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
